// components/Expertise.js
import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faBolt,
  faLaptopCode,
  faChartLine,
  faClipboardCheck,
  faTools
} from '@fortawesome/free-solid-svg-icons';
import './Expertise.css';

const Expertise = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const expertises = [
    {
      title: t('BTP_card_titre_Notre_expertise'),
      description: t('BTP_card_msg_Notre_expertise'),
      icon: faBuilding,
      color: '#0066cc',
      gradient: 'linear-gradient(135deg, #0066cc, #4d94ff)'
    },
    {
      title: t('electriq_card_titre_Notre_expertise'),
      description: t('electriq_card_msg_Notre_expertise'),
      icon: faBolt,
      color: '#f39c12',
      gradient: 'linear-gradient(135deg, #f39c12, #f1c40f)'
    },
    {
      title: t('informatiq_card_titre_Notre_expertise'),
      description: t('informatiq_card_msg_Notre_expertise'),
      icon: faLaptopCode,
      color: '#27ae60',
      gradient: 'linear-gradient(135deg, #27ae60, #2ecc71)'
    },
    {
      title: t('marketing_card_titre_Notre_expertise'),
      description: t('marketing_card_msg_Notre_expertise'),
      icon: faChartLine,
      color: '#9b59b6',
      gradient: 'linear-gradient(135deg, #9b59b6, #af7ac5)'
    },
    {
      title: t('consulting_card_titre_Notre_expertise'),
      description: t('consulting_card_msg_Notre_expertise'),
      icon: faClipboardCheck,
      color: '#e67e22',
      gradient: 'linear-gradient(135deg, #e67e22, #f39c12)'
    },
    {
      title: t('Maintenance_card_titre_Notre_expertise'),
      description: t('maintenance_card_msg_Notre_expertise'),
      icon: faTools,
      color: '#2c3e50',
      gradient: 'linear-gradient(135deg, #2c3e50, #5d6d7e)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.92
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  const glowVariants = {
    hover: {
      scale: 1.5,
      opacity: 0.6,
      transition: { duration: 0.5 }
    },
    rest: {
      scale: 1,
      opacity: 0
    }
  };

  return (
    <section className="expertise-section" ref={ref}>
      <div className="container">
        <div className="section-header">
          <motion.span
            className="section-tag"
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            {t('titre_Notre_expertise')}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {t('msg_Notre_expertise')}
          </motion.h2>
          <motion.p
            className="section-subtitle"
            initial={{ opacity: 0, y: 15 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t('msg2_Notre_expertise')}
            
          </motion.p>
        </div>

        <motion.div
          className="expertise-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {expertises.map((exp, index) => {
            const rgb = exp.color.match(/\d+/g);
            const rgbStr = rgb ? `${rgb[0]}, ${rgb[1]}, ${rgb[2]}` : '0, 102, 204';

            return (
              <motion.div
                key={index}
                className="expertise-card"
                variants={cardVariants}
                whileHover="hover"
                initial="rest"
                animate="rest"
                style={{
                  '--card-color': exp.color,
                  '--card-rgb': rgbStr,
                  '--card-gradient': exp.gradient
                }}
              >
                {/* Glow en arrière-plan */}
                <motion.div
                  className="card-glow"
                  style={{
                    background: `radial-gradient(circle, ${exp.color}33 0%, transparent 70%)`
                  }}
                  variants={glowVariants}
                  initial="rest"
                  animate="rest"
                />

                <div className="card-content">
                  <motion.div
                    className="icon-ring"
                    whileHover={{
                      scale: 1.1,
                      rotate: [0, -5, 5, -3, 3, 0],
                      transition: { duration: 0.6 }
                    }}
                  >
                    <div className="icon-wrapper" style={{ background: exp.gradient }}>
                      <FontAwesomeIcon icon={exp.icon} className="expertise-icon" />
                    </div>
                  </motion.div>

                  <h3>{exp.title}</h3>
                  <p>{exp.description}</p>

                  <motion.div
                    className="card-footer"
                    whileHover={{ gap: '12px' }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.span
                      className="btn-line"
                      style={{ background: exp.gradient }}
                      whileHover={{ width: '50px' }}
                      transition={{ duration: 0.3 }}
                    />
                    <motion.a
                      href="#"
                      className="btn-expertise"
                      whileHover={{ x: 6 }}
                      transition={{ duration: 0.2 }}
                      style={{ color: exp.color }}
                    >
                      {t('btn_Decouvrir')}
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </motion.a>
                  </motion.div>
                </div>

                {/* Ligne d'accent en bas */}
                <motion.div
                  className="card-accent"
                  style={{ background: exp.gradient }}
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Expertise;